import * as Dialog from "@radix-ui/react-dialog";
import { useState, type ReactNode } from "react";

interface ConfirmDialogProps {
  /** Inline trigger (button etc.). Mutually exclusive with `defaultOpen`/
   *  `onOpenChange` — pick one. */
  trigger?: ReactNode;
  /** Mount the dialog already open. Use together with `onOpenChange` to let
   *  the parent control when to unmount (e.g. after the dialog closes). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => Promise<void> | void;
  testid?: string;
}

export function ConfirmDialog({
  trigger,
  defaultOpen = false,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Abbrechen",
  onConfirm,
  testid,
}: ConfirmDialogProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [pending, setPending] = useState(false);

  function setBoth(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  async function handleConfirm() {
    setPending(true);
    try {
      await onConfirm();
      setBoth(false);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={setBoth}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          data-testid={testid ?? "confirm-dialog"}
          className="
            fixed z-50 bg-elevated shadow-modal p-5 space-y-4
            left-0 right-0 bottom-0 rounded-t-sheet border-t border-outline-strong animate-sheet-up
            sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:right-auto sm:w-[440px]
            sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-sheet sm:border sm:animate-slide-up
            safe-bottom sm:pb-5
          "
        >
          <div className="sm:hidden mx-auto -mt-1 mb-2 h-1.5 w-10 rounded-full bg-outline" aria-hidden />
          <Dialog.Title className="text-lg font-bold text-fg">{title}</Dialog.Title>
          <Dialog.Description className="text-sm text-fg-muted">
            {description}
          </Dialog.Description>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={pending}
                data-testid="confirm-cancel"
                className="min-h-[48px] rounded-xl bg-bg/40 border border-outline text-fg text-sm font-semibold hover:bg-card disabled:opacity-60"
              >
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={pending}
              data-testid="confirm-ok"
              className="min-h-[48px] rounded-xl bg-danger text-accent-on text-sm font-semibold hover:bg-danger/90 disabled:opacity-60"
            >
              {pending ? "…" : confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
